/**
 * Payment-Link form modal.
 *
 * Extracted from PaymentLinksList.jsx during the iteration-49 refactor
 * (PaymentLinksList was >1600 LOC → this file owns the entire "edit link"
 * dialog + its supporting sub-components).
 *
 * Public export: `LinkFormModal` (default).
 *
 * Internal helpers exported alongside because a couple of them
 * (CollapsibleSection, ProtectionPresetSelector) are useful in future
 * partner-facing configuration surfaces:
 *   - `CollapsibleSection` — accordion-style form section wrapper.
 *   - `PROTECTION_PRESETS`  — canonical list of QR-protection presets.
 *   - `ProtectionPresetSelector` — the "3 preset" picker used inside.
 *   - `LauncherAbStatsWidget` — live A/B stats for the launcher toggle.
 *   - `LinkExperimentsPanel`   — full experiments manager for one link.
 *   - `VARIANT_KEY_OPTIONS`   — allowlist of experiment variant keys
 *      (mirrors EXPERIMENT_VARIANT_KEYS on the backend).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, ChevronDown, Copy, Loader2, ShieldCheck } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import Modal from "@/components/Modal";
import DepositCountPicker, { fmtCategories as fmtDepositCats } from "@/components/DepositCountPicker";

// Generate a sensible default link_code from the partner's custom domain
// and display name. Strips common subdomain prefixes (pay/payment/checkout/www)
// and the TLD, slugifies the rest, and clamps to 40 chars.
//   ("pay.samlassicafe.com", "Branch2")        → "samlassicafe-branch2"
//   ("",                     "Bet1 Sports")    → "bet1-sports"
//   ("pay.bet1.acme.com",    "")               → "bet1-acme"
const slugifyLinkCode = (domain, name) => {
    const slug = (s) => (s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    let domainPart = "";
    if (domain) {
        const stripped = domain.toLowerCase().trim()
            .replace(/^https?:\/\//, "")
            .replace(/^(pay|payments?|checkout|www)\./, "");
        // Drop the TLD: "samlassicafe.com" → "samlassicafe"
        const labels = stripped.split(".").filter(Boolean);
        if (labels.length >= 2) labels.pop();      // drop tld
        domainPart = slug(labels.join("-"));
    }
    const namePart = slug(name);
    const combined = [domainPart, namePart].filter(Boolean).join("-").replace(/-+/g, "-");
    return combined.slice(0, 40);
};

/**
 * Lightweight collapsible section wrapper used inside LinkFormModal.
 * Header is always visible and carries a small "summary" — typically a
 * derived one-line description of the section's current state (e.g.
 * "Standard protection", "Both UPI & Bank"). Body is the actual form
 * controls and is hidden when collapsed.
 *
 * State is local — each section opens/closes independently. Default is
 * `defaultOpen` per section so partners see the most-changed sections
 * pre-expanded.
 */
function CollapsibleSection({ title, subtitle, summary, defaultOpen = false, testid, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border-t border-slate-200 pt-3" data-testid={testid}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid={testid ? `${testid}-toggle` : undefined}
                className="w-full flex items-center justify-between gap-2 text-left group"
                aria-expanded={open}
            >
                <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {title}{subtitle && <span className="text-slate-400 normal-case"> · {subtitle}</span>}
                    </div>
                    {!open && summary && (
                        <div data-testid={testid ? `${testid}-summary` : undefined}
                             className="text-[11px] text-slate-700 mt-0.5 truncate">
                            {summary}
                        </div>
                    )}
                </div>
                <ChevronDown
                    className={`w-4 h-4 text-slate-400 group-hover:text-slate-600 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                    aria-hidden="true"
                />
            </button>
            {open && (
                <div className="mt-2">
                    {children}
                </div>
            )}
        </div>
    );
}


/**
 * 3-preset selector for the per-link anti-copy protection flags:
 *   - "Open"        → all three OFF — fastest customer experience
 *   - "Standard"    → qr_anti_screenshot ON only — recommended default
 *   - "Locked-down" → all three ON — for high-value links
 *
 * Underneath, an "Advanced" expander still exposes the individual
 * checkboxes for partners who want fine-grained control.
 *
 * The component is uncontrolled w.r.t. the preset — instead, the active
 * preset is *derived* from the current values of the three flags so we
 * never have to keep a separate "preset" state in sync. If the flags
 * don't match any preset (e.g. a partner toggled one in Advanced), we
 * display "Custom" instead of misleadingly highlighting a preset.
 */
const PROTECTION_PRESETS = [
    {
        id: "open",
        label: "Open",
        tagline: "Fastest checkout",
        helper: "No anti-copy protection. Customers see a plain static QR + visible UPI ID. Best when speed matters more than guarding the QR.",
        flags: { qr_anti_screenshot: false, qr_hold_to_reveal: false, hide_upi_id: false },
        accent: "text-slate-700",
    },
    {
        id: "standard",
        label: "Standard",
        tagline: "Recommended",
        helper: "Flicker QR active — single screenshots can't be scanned. UPI ID and tap-to-view remain frictionless.",
        flags: { qr_anti_screenshot: true, qr_hold_to_reveal: false, hide_upi_id: false },
        accent: "text-emerald-700",
    },
    {
        id: "locked",
        label: "Locked-down",
        tagline: "High-value links",
        helper: "All three protections ON. Flicker QR + tap-and-hold to view + UPI ID hidden. Defeats casual copy attacks at the cost of a few extra taps.",
        flags: { qr_anti_screenshot: true, qr_hold_to_reveal: true, hide_upi_id: true },
        accent: "text-rose-700",
    },
];

function ProtectionPresetSelector({ form, setForm }) {
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Derive the current active preset by comparing the three flags to each
    // preset's flag set. If no preset matches exactly we fall back to "custom".
    const activePresetId = (() => {
        const cur = {
            qr_anti_screenshot: !!form.qr_anti_screenshot,
            qr_hold_to_reveal: !!form.qr_hold_to_reveal,
            hide_upi_id: !!form.hide_upi_id,
        };
        const match = PROTECTION_PRESETS.find((p) =>
            p.flags.qr_anti_screenshot === cur.qr_anti_screenshot &&
            p.flags.qr_hold_to_reveal === cur.qr_hold_to_reveal &&
            p.flags.hide_upi_id === cur.hide_upi_id
        );
        return match?.id || "custom";
    })();

    const pickPreset = (preset) => {
        setForm({ ...form, ...preset.flags });
    };

    return (
        <div className="space-y-2" data-testid="protection-preset-selector">
            <div className="grid grid-cols-3 gap-1.5">
                {PROTECTION_PRESETS.map((p) => {
                    const isActive = activePresetId === p.id;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            data-testid={`protection-preset-${p.id}`}
                            onClick={() => pickPreset(p)}
                            className={`text-left rounded-md border-2 px-2.5 py-2 transition-all ${isActive
                                ? `border-current bg-white shadow-sm ${p.accent}`
                                : "border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 text-slate-700"}`}
                            aria-pressed={isActive}
                        >
                            <div className="flex items-baseline justify-between gap-1">
                                <div className="text-xs font-semibold">{p.label}</div>
                                {isActive && <Check className="w-3 h-3 flex-shrink-0" />}
                            </div>
                            <div className="text-[10px] font-medium opacity-80 mt-0.5">{p.tagline}</div>
                        </button>
                    );
                })}
            </div>

            {/* Helper copy for the active preset (or for "Custom"). */}
            <div className="text-[10px] text-slate-500 leading-snug px-0.5">
                {activePresetId === "custom" ? (
                    <span><span className="font-semibold text-amber-700">Custom · </span> Your flags don&apos;t match any preset. Use Advanced below to fine-tune, or pick a preset to start fresh.</span>
                ) : (
                    PROTECTION_PRESETS.find((p) => p.id === activePresetId)?.helper
                )}
            </div>

            {/* Advanced toggle (collapsible) — keeps the original 3 checkboxes
                available for partners who want explicit control. */}
            <button
                type="button"
                data-testid="protection-advanced-toggle"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-[10px] text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline pt-0.5"
                aria-expanded={showAdvanced}
            >
                {showAdvanced ? "− Hide advanced" : "+ Advanced (fine-tune each flag)"}
            </button>
            {showAdvanced && (
                <div className="space-y-2 pl-1 border-l-2 border-slate-100 ml-0.5 pt-1" data-testid="protection-advanced-panel">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            id="lf-qr-anti-screenshot"
                            data-testid="lf-qr-anti-screenshot"
                            type="checkbox"
                            className="mt-0.5"
                            checked={!!form.qr_anti_screenshot}
                            onChange={(e) => setForm({ ...form, qr_anti_screenshot: e.target.checked })}
                        />
                        <div>
                            <div className="text-xs font-medium text-slate-800">Anti-screenshot QR (flicker / temporal multiplexing)</div>
                            <div className="text-[10px] text-slate-500 leading-snug">QR renders across two complementary noise-corrupted frames at ~60fps — any single screenshot captures only half the modules and is unscannable. <span className="text-amber-700">Turn OFF if customers report a &quot;shimmery&quot; QR.</span></div>
                        </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            id="lf-qr-hold"
                            data-testid="lf-qr-hold"
                            type="checkbox"
                            className="mt-0.5"
                            checked={!!form.qr_hold_to_reveal}
                            onChange={(e) => setForm({ ...form, qr_hold_to_reveal: e.target.checked })}
                        />
                        <div>
                            <div className="text-xs font-medium text-slate-800">Require tap-and-hold to view QR</div>
                            <div className="text-[10px] text-slate-500 leading-snug">QR is covered until customer presses and holds. Releasing instantly re-covers — defeats casual screenshot capture.</div>
                        </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            id="lf-hide-upi"
                            data-testid="lf-hide-upi"
                            type="checkbox"
                            className="mt-0.5"
                            checked={!!form.hide_upi_id}
                            onChange={(e) => setForm({ ...form, hide_upi_id: e.target.checked })}
                        />
                        <div>
                            <div className="text-xs font-medium text-slate-800">Hide UPI ID on the payment page</div>
                            <div className="text-[10px] text-slate-500 leading-snug">Customer can only pay via QR scan or UPI app intent buttons. The plain UPI ID won&apos;t be displayed or copyable.</div>
                        </div>
                    </label>
                </div>
            )}

            {/* QR download — independent of the preset (it's an anti-protection
                feature, not a protection one). Kept separate so the Open/
                Standard/Locked presets don't accidentally toggle it. */}
            <label className="flex items-start gap-2 cursor-pointer pt-1 mt-1 border-t border-slate-200">
                <input
                    id="lf-qr-allow-download"
                    data-testid="lf-qr-allow-download"
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!form.qr_allow_download}
                    onChange={(e) => setForm({ ...form, qr_allow_download: e.target.checked })}
                />
                <div>
                    <div className="text-xs font-medium text-slate-800">Allow customers to download the QR (PNG)</div>
                    <div className="text-[10px] text-slate-500 leading-snug">Shows a small &quot;Download QR&quot; link below the QR on the payment page. <span className="text-amber-700">Note: defeats anti-screenshot protection — only enable if customers genuinely need to save the QR and pay later from another device.</span></div>
                </div>
            </label>
            <label className={`flex items-start gap-2 cursor-pointer pl-6 ${form.qr_allow_download ? "" : "opacity-50"}`}>
                <input
                    id="lf-qr-download-with-caption"
                    data-testid="lf-qr-download-with-caption"
                    type="checkbox"
                    className="mt-0.5"
                    disabled={!form.qr_allow_download}
                    checked={!!form.qr_download_with_caption}
                    onChange={(e) => setForm({ ...form, qr_download_with_caption: e.target.checked })}
                />
                <div>
                    <div className="text-xs font-medium text-slate-800">Bake &quot;Pay ₹X to {"{merchant}"}&quot; caption into the downloaded PNG</div>
                    <div className="text-[10px] text-slate-500 leading-snug">The recipient (friend / family paying on behalf of the customer) sees the amount + merchant baked into the image at a glance — eliminates miscommunication. Pure frontend canvas composition, no server cost. Requires QR download to be ON.</div>
                </div>
            </label>

            {/* Auto-extend session timer on share/download. Independent of
                the anti-copy preset — this is a conversion optimisation, not
                a protection one. Sits here next to qr_allow_download because
                the share telemetry that triggers it comes from the same
                download/share path. */}
            <label className="flex items-start gap-2 cursor-pointer pt-1 mt-1 border-t border-slate-200">
                <input
                    id="lf-extend-on-share"
                    data-testid="lf-extend-on-share"
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!form.extend_on_share}
                    onChange={(e) => setForm({ ...form, extend_on_share: e.target.checked })}
                />
                <div>
                    <div className="text-xs font-medium text-slate-800">Auto-extend session timer when customer shares the QR</div>
                    <div className="text-[10px] text-slate-500 leading-snug">Shared QRs typically take 3–5× longer to convert (the friend has to be reached on WhatsApp/Telegram first). Extending the timer prevents the shared-cohort from expiring before the friend actually opens the QR. Only fires once per transaction.</div>
                </div>
            </label>
            <div className={`pl-6 ${form.extend_on_share ? "" : "opacity-50"}`}>
                <label className="flex items-center gap-2 text-[11px] text-slate-600">
                    <span>Extension duration:</span>
                    <input
                        id="lf-extend-on-share-minutes"
                        data-testid="lf-extend-on-share-minutes"
                        type="number"
                        min={1}
                        max={120}
                        disabled={!form.extend_on_share}
                        value={form.extend_on_share_minutes ?? 15}
                        onChange={(e) => {
                            const v = parseInt(e.target.value || "15", 10);
                            setForm({ ...form, extend_on_share_minutes: Number.isFinite(v) ? Math.max(1, Math.min(120, v)) : 15 });
                        }}
                        className="w-16 px-2 py-1 border border-slate-300 rounded text-xs font-mono disabled:bg-slate-100 disabled:text-slate-400"
                    />
                    <span className="text-slate-500">minutes (1–120)</span>
                </label>
            </div>
        </div>
    );
}



/**
 * Compact A/B stats widget for the `manual_upi_launcher_mode = "ab_test"`
 * variant. Polls `/partner/payment-links/{link_id}/launcher-ab-stats` and
 * surfaces visitor count, UTR rate, and success rate per cohort.
 *
 * Renders nothing when `scope === "admin"` (the endpoint is partner-scoped
 * for now) or when `linkId` is falsy (new-link form, before save).
 */
function LauncherAbStatsWidget({ linkId, scope, abEnabled }) {
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const canFetch = scope !== "admin" && !!linkId && abEnabled;
    useEffect(() => {
        if (!canFetch) { setStats(null); setError(null); return; }
        let alive = true;
        const fetchStats = async () => {
            setLoading(true);
            try {
                const r = await api.get(`/partner/payment-links/${linkId}/launcher-ab-stats`);
                if (alive) { setStats(r.data); setError(null); }
            } catch (e) {
                if (alive) setError(formatDetail(e) || "Failed to load A/B stats");
            } finally { if (alive) setLoading(false); }
        };
        fetchStats();
        // Light polling — refresh every 15s while the modal is open. The
        // experiment moves slowly (UTR submission lags visitor by minutes),
        // so we don't need real-time updates.
        const t = setInterval(fetchStats, 15000);
        return () => { alive = false; clearInterval(t); };
    }, [linkId, canFetch]);

    if (!abEnabled) return null;
    if (scope === "admin") {
        return (
            <div className="mt-1.5 ml-6 text-[10px] text-slate-500 italic">
                A/B stats are visible in the partner&apos;s own dashboard.
            </div>
        );
    }
    if (!linkId) {
        return (
            <div className="mt-1.5 ml-6 text-[10px] text-slate-500 italic">
                Save this link first to see live A/B stats.
            </div>
        );
    }
    if (loading && !stats) {
        return <div className="mt-1.5 ml-6 text-[10px] text-slate-500">Loading A/B stats…</div>;
    }
    if (error) {
        return <div className="mt-1.5 ml-6 text-[10px] text-rose-600">{error}</div>;
    }
    if (!stats) return null;

    const v = stats.variants || {};
    const w = v.with_launchers || {};
    const wo = v.without_launchers || {};
    const totalVisitors = stats.total_visitors || 0;
    const winnerKey = (() => {
        if (!w.visitors || !wo.visitors) return null;
        if ((w.success_rate || 0) > (wo.success_rate || 0)) return "with";
        if ((wo.success_rate || 0) > (w.success_rate || 0)) return "without";
        return null;
    })();

    return (
        <div data-testid="launcher-ab-stats" className="mt-2 ml-6 rounded-md bg-slate-50 border border-slate-200 px-2 py-2">
            <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] font-semibold text-slate-700 uppercase tracking-wider">Live A/B results</div>
                <div className="text-[9px] text-slate-500">{totalVisitors} visitor{totalVisitors === 1 ? "" : "s"} bucketed</div>
            </div>
            <div className="flex gap-1.5">
                <AbStatsCell title="With launchers" data={w} isWinner={winnerKey === "with"} />
                <AbStatsCell title="Without launchers" data={wo} isWinner={winnerKey === "without"} />
            </div>
            {totalVisitors < 30 && (
                <div className="mt-1.5 text-[10px] text-amber-700 leading-snug">
                    Sample size still small (need ~30+ visitors per cohort for a reliable signal).
                </div>
            )}
        </div>
    );
}

// Hoisted out of `LauncherAbStatsWidget` so React doesn't tear down the
// subtree on every parent re-render (react/no-unstable-nested-components).
function AbStatsCell({ title, data, isWinner }) {
    return (
        <div data-testid={`ab-stats-${title.toLowerCase().replace(/\s+/g, "-")}`}
             className={`flex-1 rounded-md border px-2 py-1.5 ${isWinner ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
            <div className="flex items-center justify-between gap-1">
                <div className="text-[10px] font-semibold text-slate-700">{title}</div>
                {isWinner && <span className="text-[8px] uppercase tracking-wider text-emerald-700 font-bold">Winner</span>}
            </div>
            <div className="grid grid-cols-3 gap-1 mt-1 text-[10px]">
                <div><div className="text-slate-500">Visitors</div><div className="font-semibold text-slate-900">{data.visitors || 0}</div></div>
                <div><div className="text-slate-500">UTR</div><div className="font-semibold text-slate-900">{data.utr_rate || 0}%</div></div>
                <div><div className="text-slate-500">Success</div><div className="font-semibold text-slate-900">{data.success_rate || 0}%</div></div>
            </div>
        </div>
    );
}

/**
 * A/B experiments panel embedded inside the Edit Payment Link modal.
 *
 * Lists active and archived experiments on this link with live per-cohort
 * stats. Lets partners spin up a new experiment in 3 clicks: pick a
 * variant_key, pick treatment A/B values, label it.
 *
 * Renders only when `scope === "partner"` (the partner endpoints are
 * partner-scoped) and a link has been saved (no link_id = nothing to
 * attach an experiment to).
 *
 * The whitelist of variant keys mirrors `EXPERIMENT_VARIANT_KEYS` on the
 * backend — adding a new key requires touching both sides.
 */
const VARIANT_KEY_OPTIONS = [
    { value: "merchant_intent", label: "PSP merchant intent (tr, mode, mc params)", type: "bool" },
    { value: "qr_hold_to_reveal", label: "QR tap-and-hold-to-reveal", type: "bool" },
    { value: "hide_upi_id", label: "Hide UPI ID on payment page", type: "bool" },
    { value: "auto_rotate_on_fail", label: "Auto-rotate channel on decline", type: "bool" },
    { value: "fallback_to_bank", label: "Fallback to bank after 2 UPI declines", type: "bool" },
    { value: "manual_upi_recopy_pill", label: "\"Copy UPI ID again\" pill", type: "bool" },
    { value: "mcc_code", label: "MCC code (e.g. 5411 vs 7011)", type: "str" },
];

function LinkExperimentsPanel({ linkId, scope }) {
    const [exps, setExps] = useState([]);
    const [stats, setStats] = useState({});  // {experiment_id: stats}
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        variant_key: "merchant_intent",
        label: "",
        treatment_a_value: false,
        treatment_b_value: true,
    });
    // Type of the currently selected variant_key — drives which input
    // control is rendered for the A/B values.
    const selectedType = (VARIANT_KEY_OPTIONS.find((o) => o.value === form.variant_key) || {}).type || "bool";
    // When the user picks a new variant_key, reset treatment values to
    // sensible defaults of the correct type so we never submit a bool to
    // a string-typed key.
    const onVariantKeyChange = (newKey) => {
        const t = (VARIANT_KEY_OPTIONS.find((o) => o.value === newKey) || {}).type || "bool";
        const defaults = t === "str"
            ? { treatment_a_value: "", treatment_b_value: "" }
            : { treatment_a_value: false, treatment_b_value: true };
        setForm({ ...form, variant_key: newKey, ...defaults });
    };
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const canShow = scope !== "admin" && !!linkId;

    const refresh = async () => {
        if (!canShow) return;
        try {
            const r = await api.get(`/partner/experiments?link_id=${encodeURIComponent(linkId)}`);
            const items = r.data?.items || [];
            setExps(items);
            // Fetch stats in parallel for active experiments only.
            const activeIds = items.filter((e) => e.status === "active").map((e) => e.experiment_id);
            const results = await Promise.all(activeIds.map((id) =>
                api.get(`/partner/experiments/${id}/stats`).then((rr) => [id, rr.data]).catch(() => [id, null])
            ));
            const map = {};
            for (const [id, s] of results) if (s) map[id] = s;
            setStats(map);
        } catch (e) { setError(formatDetail(e) || "Failed to load experiments"); }
    };

    useEffect(() => {
        if (!canShow) return;
        refresh();
        // Poll every 15s while modal open — variant deltas show up live.
        const t = setInterval(refresh, 15000);
        return () => clearInterval(t);
     
    }, [linkId, canShow]);

    const create = async (e) => {
        e?.preventDefault?.();
        if (!form.label.trim()) { setError("Label is required"); return; }
        if (form.treatment_a_value === form.treatment_b_value) {
            setError("Treatment A and B must differ"); return;
        }
        setBusy(true); setError(null);
        try {
            await api.post(`/partner/experiments`, {
                link_id: linkId,
                variant_key: form.variant_key,
                label: form.label.trim(),
                treatment_a_value: form.treatment_a_value,
                treatment_b_value: form.treatment_b_value,
            });
            setShowForm(false);
            setForm({ ...form, label: "" });
            refresh();
        } catch (er) { setError(formatDetail(er?.response?.data?.detail) || er.message); }
        finally { setBusy(false); }
    };

    const archive = async (expId) => {
        if (!window.confirm("Archive this experiment? Variant assignments on past txns will be preserved but no new visitors will be bucketed.")) return;
        try {
            await api.delete(`/partner/experiments/${expId}`);
            refresh();
        } catch (er) { setError(formatDetail(er?.response?.data?.detail) || er.message); }
    };

    if (!canShow) {
        return (
            <div className="border-t border-slate-200 pt-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                    A/B experiments
                </div>
                <div className="text-[10px] text-slate-500 italic">
                    {scope === "admin"
                        ? "Visible in the partner's own dashboard."
                        : "Save this link first to spin up experiments."}
                </div>
            </div>
        );
    }

    const active = exps.filter((e) => e.status === "active");
    const archived = exps.filter((e) => e.status === "archived");
    const usedActiveKeys = new Set(active.map((e) => e.variant_key));

    return (
        <div className="border-t border-slate-200 pt-3" data-testid="link-experiments-panel">
            <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    A/B experiments <span className="text-slate-400 normal-case">· {active.length} active</span>
                </div>
                {!showForm && (
                    <button type="button" data-testid="exp-new-btn"
                            onClick={() => { setError(null); setShowForm(true); }}
                            className="text-[11px] btn-secondary px-2 py-1 rounded-md">
                        + New experiment
                    </button>
                )}
            </div>

            {showForm && (
                <div data-testid="exp-form" className="rounded-md border border-blue-200 bg-blue-50/40 p-2.5 mb-2 space-y-2">
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-600 font-semibold mb-0.5">Variant to test</label>
                        <select data-testid="exp-form-key"
                                className="input !text-xs w-full"
                                value={form.variant_key}
                                onChange={(e) => onVariantKeyChange(e.target.value)}>
                            {VARIANT_KEY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}
                                        disabled={usedActiveKeys.has(o.value)}>
                                    {o.label}{usedActiveKeys.has(o.value) ? " (already running)" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-600 font-semibold mb-0.5">Label</label>
                        <input type="text" data-testid="exp-form-label"
                               className="input !text-xs w-full"
                               placeholder="e.g. PSP intent on/off"
                               value={form.label}
                               onChange={(e) => setForm({ ...form, label: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {["a", "b"].map((side) => {
                            const field = `treatment_${side}_value`;
                            // Render a type-appropriate input. For bool we
                            // keep the simple select; for str we use a
                            // text input with placeholder hints per
                            // variant_key.
                            const placeholders = {
                                mcc_code: side === "a" ? "e.g. 5411 (groceries)" : "e.g. 7011 (hotels)",
                            };
                            return (
                                <div key={side}>
                                    <label className="block text-[10px] uppercase tracking-wider text-slate-600 font-semibold mb-0.5">
                                        Treatment {side.toUpperCase()} {side === "a" ? "(control)" : "(variant)"}
                                    </label>
                                    {selectedType === "bool" ? (
                                        <select data-testid={`exp-form-${field}`}
                                                className="input !text-xs w-full"
                                                value={String(form[field])}
                                                onChange={(e) => setForm({ ...form, [field]: e.target.value === "true" })}>
                                            <option value="false">false</option>
                                            <option value="true">true</option>
                                        </select>
                                    ) : (
                                        <input type="text"
                                               data-testid={`exp-form-${field}`}
                                               className="input !text-xs w-full"
                                               placeholder={placeholders[form.variant_key] || ""}
                                               value={form[field] ?? ""}
                                               maxLength={120}
                                               onChange={(e) => setForm({ ...form, [field]: e.target.value })} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {error && <div className="text-rose-600 text-[11px]">{error}</div>}
                    <div className="flex gap-2 justify-end">
                        <button type="button" onClick={() => { setShowForm(false); setError(null); }}
                                className="btn-secondary text-[11px] px-2.5 py-1 rounded-md">Cancel</button>
                        <button type="button" data-testid="exp-form-save"
                                onClick={create} disabled={busy}
                                className="btn-primary text-[11px] px-2.5 py-1 rounded-md flex items-center gap-1">
                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            Start experiment
                        </button>
                    </div>
                </div>
            )}

            {active.length === 0 && !showForm && (
                <div className="text-[10px] text-slate-500 italic">
                    No active experiments. Spin one up to A/B test any of the variant flags above.
                </div>
            )}

            <div className="space-y-1.5">
                {active.map((exp) => {
                    const s = stats[exp.experiment_id];
                    const va = s?.variants?.a || {};
                    const vb = s?.variants?.b || {};
                    const total = s?.total_visitors || 0;
                    const winnerKey = (() => {
                        if (!va.visitors || !vb.visitors) return null;
                        if ((va.success_rate || 0) > (vb.success_rate || 0)) return "a";
                        if ((vb.success_rate || 0) > (va.success_rate || 0)) return "b";
                        return null;
                    })();
                    return (
                        <div key={exp.experiment_id} data-testid={`exp-card-${exp.experiment_id}`}
                             className="rounded-md border border-slate-200 bg-white p-2">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                                <div className="min-w-0">
                                    <div className="text-xs font-medium text-slate-800 truncate">{exp.label}</div>
                                    <div className="text-[10px] text-slate-500 font-mono">
                                        {exp.variant_key} · A={String(exp.treatment_a_value)} / B={String(exp.treatment_b_value)}
                                    </div>
                                </div>
                                <button type="button" data-testid={`exp-archive-${exp.experiment_id}`}
                                        onClick={() => archive(exp.experiment_id)}
                                        className="text-[10px] text-rose-600 hover:bg-rose-50 px-1.5 py-0.5 rounded">
                                    Stop
                                </button>
                            </div>
                            <div className="flex gap-1.5">
                                {["a", "b"].map((b) => {
                                    const d = b === "a" ? va : vb;
                                    const isWin = winnerKey === b;
                                    return (
                                        <div key={b} className={`flex-1 rounded border px-2 py-1 ${isWin ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="text-[10px] font-semibold text-slate-700">{b.toUpperCase()}</div>
                                                {isWin && <span className="text-[8px] uppercase tracking-wider text-emerald-700 font-bold">Winner</span>}
                                            </div>
                                            <div className="grid grid-cols-3 gap-1 mt-0.5 text-[9px]">
                                                <div><div className="text-slate-500">Visitors</div><div className="font-semibold">{d.visitors || 0}</div></div>
                                                <div><div className="text-slate-500">UTR</div><div className="font-semibold">{d.utr_rate || 0}%</div></div>
                                                <div><div className="text-slate-500">Win</div><div className="font-semibold">{d.success_rate || 0}%</div></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {total < 30 && (
                                <div className="text-[9px] text-amber-700 mt-1">
                                    Sample {total} — need ~30+ per cohort for a reliable signal.
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {archived.length > 0 && (
                <details className="mt-1.5">
                    <summary className="text-[10px] text-slate-500 cursor-pointer hover:text-slate-700">Archived ({archived.length})</summary>
                    <div className="space-y-0.5 mt-1">
                        {archived.map((exp) => (
                            <div key={exp.experiment_id} className="text-[10px] text-slate-500 font-mono pl-2">
                                {exp.variant_key} · {exp.label}
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}


export default function LinkFormModal({ scope, baseLink, linkOps, fallbackName, link, channels, websites = [], onClose, onSaved }) {
    const isNew = !link;
    const [f, setF] = useState({
        link_code: link?.link_code || "",
        display_name: link?.display_name || fallbackName || "",
        custom_domain: link?.custom_domain || "",
        success_redirect: link?.success_redirect || "",
        failure_redirect: link?.failure_redirect || "",
        require_username: link?.require_username ?? true,
        status: link?.status || "active",
        allowed_upi_channels: link?.allowed_upi_channels || [],
        allowed_bank_accounts: link?.allowed_bank_accounts || [],
        // iter-66: optional per-link deposit-count filter. Empty = any
        // deposit index passes (backward-compat). Non-empty enforces
        // the same 0-indexed allow-list channels already use.
        allowed_deposit_counts: link?.allowed_deposit_counts || [],
        // iter-67: optional 1-indexed "N and above" threshold that
        // stacks on top of allowed_deposit_counts. null = disabled.
        min_deposit_count: link?.min_deposit_count ?? null,
        logo_data_url: link?.logo_data_url || "",
        enabled_methods: link?.enabled_methods || "all",
        qr_hold_to_reveal: link?.qr_hold_to_reveal || false,
        // Default true preserves the current behaviour for links created
        // before this flag existed. The `??` is critical — `||` would
        // accidentally flip an explicit `false` (merchant disabled) back
        // to true on every form load.
        qr_anti_screenshot: link?.qr_anti_screenshot ?? true,
        qr_allow_download: link?.qr_allow_download || false,
        qr_download_with_caption: link?.qr_download_with_caption ?? true,
        hide_upi_id: link?.hide_upi_id || false,
        merchant_intent: link?.merchant_intent || false,
        mcc_code: link?.mcc_code || "",
        auto_rotate_on_fail: link?.auto_rotate_on_fail || false,
        manual_upi_mode: link?.manual_upi_mode || false,
        // 3-state launcher mode: "off" | "on" | "ab_test". Derive from
        // legacy bool if the new field isn't set on this link yet.
        manual_upi_launcher_mode: link?.manual_upi_launcher_mode || (link?.manual_upi_hide_launchers ? "on" : "off"),
        manual_upi_recopy_pill: link?.manual_upi_recopy_pill || false,
        extend_on_share: link?.extend_on_share || false,
        extend_on_share_minutes: link?.extend_on_share_minutes ?? 15,
        allowed_website_ids: link?.allowed_website_ids || [],
        default_website_id: link?.default_website_id || null,
    });
    // Track whether the user has manually edited link_code. We auto-derive it
    // from (custom_domain + display_name) on new links until the user types
    // their own value — at which point we stop overwriting it.
    const [codeTouched, setCodeTouched] = useState(!isNew);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [verifyMsg, setVerifyMsg] = useState("");
    const [dnsCopied, setDnsCopied] = useState(false);

    const copyDnsInstruction = () => {
        const text = `${f.custom_domain} CNAME ${window.location.host}`;
        const done = () => { setDnsCopied(true); setTimeout(() => setDnsCopied(false), 1500); };
        try {
            if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(() => {
                    const ta = document.createElement("textarea");
                    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
                    document.body.appendChild(ta); ta.select();
                    try { document.execCommand("copy"); done(); } catch { /* ignore */ }
                    ta.remove();
                });
            }
        } catch { /* ignore */ }
    };

    // Auto-fill link_code reactively whenever the source fields change,
    // unless the user has taken control of the field.
    useEffect(() => {
        if (codeTouched) return;
        const next = slugifyLinkCode(f.custom_domain, f.display_name);
        if (next && next !== f.link_code) setF((s) => ({ ...s, link_code: next }));
         
    }, [f.custom_domain, f.display_name, codeTouched]);

    // Logo upload — restrict to image/* and 150 KB to keep DB rows small.
    const onLogoUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setErr("Logo must be an image (PNG, JPG, SVG, WebP).");
            return;
        }
        if (file.size > 150 * 1024) {
            setErr("Logo must be smaller than 150 KB. Compress it (try tinypng.com) and retry.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setErr("");
            setF((s) => ({ ...s, logo_data_url: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    const toggleChan = (kind, id) => {
        const key = kind === "upi" ? "allowed_upi_channels" : "allowed_bank_accounts";
        setF((s) => ({ ...s,
            [key]: s[key].includes(id) ? s[key].filter((x) => x !== id) : [...s[key], id],
        }));
    };

    const save = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        const payload = {
            ...f,
            link_code: f.link_code.trim().toLowerCase(),
            custom_domain: f.custom_domain.trim() || null,
            success_redirect: f.success_redirect.trim() || null,
            failure_redirect: f.failure_redirect.trim() || null,
        };
        try {
            if (isNew) await api.post(baseLink, payload);
            else await api.patch(linkOps(link.link_id), payload);
            onSaved();
        } catch (e2) {
            setErr(formatDetail(e2?.response?.data?.detail) || e2.message);
        } finally { setBusy(false); }
    };

    const verify = async () => {
        if (isNew) return;
        // iter-71: warn if the domain typed in the form doesn't match
        // what's saved in the DB — the backend reads from the DB, so
        // clicking Verify DNS on unsaved edits gives a confusing
        // "No custom_domain configured for this link" message.
        const typed = (f.custom_domain || "").trim().toLowerCase();
        const saved = ((link?.custom_domain) || "").trim().toLowerCase();
        if (typed !== saved) {
            setVerifyMsg(
                `⚠️ Save the link first — you typed "${typed}" but the saved value is "${saved || "(empty)"}". Click Save, then Verify DNS.`
            );
            return;
        }
        setVerifyMsg("⏳ Verifying DNS…");
        try {
            const r = await api.post(`${linkOps(link.link_id)}/verify-domain`);
            const { ok, reason, expected_target } = r.data;
            setVerifyMsg(ok
                ? `✅ Verified — ${reason}`
                : `❌ ${reason}${expected_target ? `. Expected CNAME → ${expected_target}.` : "."}`);
        } catch (e2) { setVerifyMsg(formatDetail(e2?.response?.data?.detail) || e2.message); }
    };

    return (
        <Modal testId="link-form-modal" onClose={onClose}
               title={isNew ? "Create payment link" : `Edit · ${link.link_code}`} size="lg">
            <form onSubmit={save} className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label">Link code {isNew && !codeTouched && (f.custom_domain || f.display_name) && (
                            <span className="ml-1 text-[10px] text-emerald-600 normal-case">· auto-generated</span>
                        )}</label>
                        <input data-testid="lf-code" required minLength={2} maxLength={40}
                               pattern="[-a-z0-9_]+"
                               className="input font-mono" value={f.link_code}
                               onChange={(e) => { setCodeTouched(true); setF({ ...f, link_code: e.target.value }); }}
                               placeholder="e.g. bet1" />
                        <div className="text-[10px] text-slate-400 mt-0.5">
                            {isNew && !codeTouched
                                ? "Auto-built from your domain + display name. Type to override."
                                : "a-z, 0-9, dash, underscore"}
                        </div>
                    </div>
                    <div>
                        <label className="label">Status</label>
                        <select data-testid="lf-status" value={f.status} className="input"
                                onChange={(e) => setF({ ...f, status: e.target.value })}>
                            <option value="active">Active</option>
                            <option value="disabled">Disabled</option>
                        </select>
                    </div>
                </div>
                <div>
                    <label className="label">Display name (shown to customer on the payment page)</label>
                    <input data-testid="lf-name" required maxLength={100} className="input"
                           value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })}
                           placeholder="e.g. Bet1 Sports" />
                </div>
                <div>
                    <label className="label">Logo <span className="text-slate-400 text-[10px]">(optional · ≤150 KB · square works best)</span></label>
                    <div className="flex items-center gap-3">
                        <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {f.logo_data_url ? (
                                <img src={f.logo_data_url} alt="logo preview" className="w-full h-full object-contain" />
                            ) : (
                                <span className="text-[10px] text-slate-400">no logo</span>
                            )}
                        </div>
                        <div className="flex-1 flex gap-2">
                            <label htmlFor="lf-logo-input"
                                   className="btn-secondary px-3 py-2 rounded-md text-xs cursor-pointer flex items-center gap-1.5">
                                Upload
                                <input id="lf-logo-input" data-testid="lf-logo-input" type="file"
                                       accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                       onChange={onLogoUpload} className="hidden" />
                            </label>
                            {f.logo_data_url && (
                                <button type="button" data-testid="lf-logo-clear"
                                        onClick={() => setF((s) => ({ ...s, logo_data_url: "" }))}
                                        className="text-xs text-rose-600 hover:bg-rose-50 px-2 py-2 rounded">
                                    Remove
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <div>
                    <label className="label">
                        Custom domain
                        {!isNew && link?.domain_status === "dns_verified" && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-700">
                                <ShieldCheck className="w-3 h-3" /> verified
                            </span>
                        )}
                        {!isNew && link?.domain_status === "dns_pending" && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-700">
                                <AlertTriangle className="w-3 h-3" /> dns pending
                            </span>
                        )}
                    </label>
                    <div className="flex gap-2">
                        <input data-testid="lf-domain" maxLength={200} className="input font-mono flex-1"
                               value={f.custom_domain} onChange={(e) => setF({ ...f, custom_domain: e.target.value })}
                               placeholder="e.g. pay.bet1.acme.com" />
                        {!isNew && f.custom_domain && (
                            <button type="button" onClick={verify}
                                    className="btn-secondary px-3 py-2 rounded-md text-xs flex items-center gap-1">
                                <ShieldCheck className="w-3.5 h-3.5" /> Verify DNS
                            </button>
                        )}
                    </div>
                    {f.custom_domain && (
                        <div className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded p-2 mt-1.5 leading-relaxed">
                            <strong>DNS setup:</strong> add a CNAME at your registrar:
                            <div className="flex items-stretch gap-1 mt-1">
                                <code className="font-mono flex-1 bg-white px-2 py-1 rounded break-all">
                                    {f.custom_domain} CNAME {window.location.host}
                                </code>
                                <button type="button"
                                        data-testid="lf-dns-copy"
                                        onClick={copyDnsInstruction}
                                        title="Copy CNAME record to paste into Cloudflare/GoDaddy/Namecheap"
                                        className={`px-2 py-1 rounded text-[10px] font-medium border flex items-center gap-1 transition-colors flex-shrink-0 ${
                                            dnsCopied
                                                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                                : "bg-white border-blue-200 text-blue-700 hover:bg-blue-100"
                                        }`}>
                                    {dnsCopied ? (<><Check className="w-3 h-3" /> Copied</>) : (<><Copy className="w-3 h-3" /> Copy</>)}
                                </button>
                            </div>
                            After saving, click <strong>Verify DNS</strong> to confirm. TLS auto-provisions on first request once verified.
                            {" "}<span className="text-slate-500">Cloudflare-proxied? Keep the orange cloud ON and set SSL/TLS mode to <strong>Full</strong> — we detect proxied setups automatically.</span>
                        </div>
                    )}
                    {verifyMsg && (
                        <div data-testid="verify-dns-msg" className={`text-[11px] mt-1.5 px-2 py-1.5 rounded border ${
                            verifyMsg.startsWith("✅") ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                            : verifyMsg.startsWith("⚠️") ? "bg-amber-50 border-amber-200 text-amber-800"
                            : verifyMsg.startsWith("⏳") ? "bg-blue-50 border-blue-200 text-blue-800"
                                                        : "bg-rose-50 border-rose-200 text-rose-800"
                        }`}>{verifyMsg}</div>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="label">Success redirect URL</label>
                        <input data-testid="lf-success" type="url" maxLength={500} className="input"
                               value={f.success_redirect} onChange={(e) => setF({ ...f, success_redirect: e.target.value })}
                               placeholder="https://your-site.com/wallet/success" />
                    </div>
                    <div>
                        <label className="label">Failure redirect URL</label>
                        <input data-testid="lf-failure" type="url" maxLength={500} className="input"
                               value={f.failure_redirect} onChange={(e) => setF({ ...f, failure_redirect: e.target.value })}
                               placeholder="https://your-site.com/wallet/failure" />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <input id="lf-req" type="checkbox" checked={f.require_username}
                           onChange={(e) => setF({ ...f, require_username: e.target.checked })} />
                    <label htmlFor="lf-req" className="text-xs">Require customer username on landing</label>
                </div>

                <CollapsibleSection
                    testid="section-anti-decline"
                    title="Anti-decline controls"
                    subtitle="reduce UPI risk-policy declines"
                    defaultOpen={false}
                    summary={(() => {
                        const on = [];
                        if (f.merchant_intent) on.push("P2M intent");
                        if (f.auto_rotate_on_fail) on.push("auto-rotate");
                        if (f.fallback_to_bank) on.push("fail→bank");
                        if (f.manual_upi_mode) on.push("manual UPI");
                        return on.length ? on.join(" · ") : "All defaults";
                    })()}
                >
                    <div className="space-y-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                id="lf-merchant-intent"
                                data-testid="lf-merchant-intent"
                                type="checkbox"
                                className="mt-0.5"
                                checked={f.merchant_intent}
                                onChange={(e) => setF({ ...f, merchant_intent: e.target.checked })}
                            />
                            <div>
                                <div className="text-xs font-medium text-slate-800">Use merchant-flow UPI intent (P2M params)</div>
                                <div className="text-[10px] text-slate-500 leading-snug">Adds <code className="font-mono">tr</code>, <code className="font-mono">mode=04</code>, <code className="font-mono">purpose=00</code>, and <code className="font-mono">mc</code> (MCC) to the intent URL. Biases PSP risk engines toward merchant-flow rules. Does NOT bypass NPCI signatures.</div>
                            </div>
                        </label>
                        <div className="ml-6 grid grid-cols-2 gap-3 items-end">
                            <div>
                                <label className="label">MCC code (optional)</label>
                                <input
                                    data-testid="lf-mcc"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="\d{3,4}"
                                    maxLength={4}
                                    placeholder="e.g. 5411"
                                    className="input !text-xs"
                                    value={f.mcc_code}
                                    onChange={(e) => setF({ ...f, mcc_code: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                                    disabled={!f.merchant_intent}
                                />
                                <div className="text-[10px] text-slate-400 mt-0.5">3-4 digit Merchant Category Code (NPCI list)</div>
                            </div>
                        </div>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                id="lf-rotate"
                                data-testid="lf-rotate"
                                type="checkbox"
                                className="mt-0.5"
                                checked={f.auto_rotate_on_fail}
                                onChange={(e) => setF({ ...f, auto_rotate_on_fail: e.target.checked })}
                            />
                            <div>
                                <div className="text-xs font-medium text-slate-800">Auto-rotate to next channel after intent decline</div>
                                <div className="text-[10px] text-slate-500 leading-snug">If the customer returns to the page from the UPI app within ~25s without paying, rotate to the next eligible UPI channel and refresh the QR. Defeats per-VPA velocity caps.</div>
                            </div>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                id="lf-fallback-bank"
                                data-testid="lf-fallback-bank"
                                type="checkbox"
                                className="mt-0.5"
                                checked={f.fallback_to_bank || false}
                                onChange={(e) => setF({ ...f, fallback_to_bank: e.target.checked })}
                            />
                            <div>
                                <div className="text-xs font-medium text-slate-800">Fall back to Bank Transfer after UPI keeps declining</div>
                                <div className="text-[10px] text-slate-500 leading-snug">If the customer hits 2 UPI declines, automatically switch them to a bank-transfer channel. Bank transfers don&apos;t go through UPI&apos;s risk engine — typically recovers ~30% of UPI-declined customers.</div>
                            </div>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                id="lf-manual-upi"
                                data-testid="lf-manual-upi"
                                type="checkbox"
                                className="mt-0.5"
                                checked={f.manual_upi_mode}
                                onChange={(e) => setF({ ...f, manual_upi_mode: e.target.checked })}
                            />
                            <div>
                                <div className="text-xs font-medium text-slate-800">Manual UPI mode (no merchant intent)</div>
                                <div className="text-[10px] text-slate-500 leading-snug">Replaces the &quot;Pay via UPI&quot; button with a &quot;Copy UPI ID + open your UPI app&quot; flow. The customer pays through the app&apos;s &quot;Send to UPI ID&quot; screen — no <code className="font-mono">upi://pay?...</code> intent fired, no merchant-flow signal to PSPs. <span className="text-emerald-700 font-medium">Significantly lower decline rates on Yes Bank / personal handles.</span></div>
                            </div>
                        </label>
                        <div className={`pl-6 space-y-1.5 ${f.manual_upi_mode ? "" : "opacity-50"}`}>
                            <div className="text-xs font-medium text-slate-800">GPay / PhonePe / Paytm launcher buttons</div>
                            <div className="text-[10px] text-slate-500 leading-snug mb-1">Launcher buttons attempt to deep-link directly into a specific UPI app. They&apos;re inherently flaky on Android (Chrome doesn&apos;t expose a sanctioned API for &quot;open app X&apos;s home screen&quot;) and can confuse customers when they fail. Pick the behaviour that fits your audience — or A/B test both.</div>
                            <div className="space-y-1">
                                {[
                                    { value: "off", label: "Show launchers (default)", hint: "One-tap-to-app convenience; accept that some Android variants will be flaky." },
                                    { value: "on", label: "Hide launchers (cleaner UX)", hint: "Only the prominent 'Copy UPI ID' button + plain instructions. No false hopes." },
                                    { value: "ab_test", label: "A/B test (50 / 50 split)", hint: "Each customer is bucketed deterministically and sticky-per-txn. Live stats below once you save." },
                                ].map((opt) => (
                                    <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="lf-manual-upi-launcher-mode"
                                            data-testid={`lf-launcher-mode-${opt.value}`}
                                            className="mt-0.5"
                                            disabled={!f.manual_upi_mode}
                                            checked={f.manual_upi_launcher_mode === opt.value}
                                            onChange={() => setF({ ...f, manual_upi_launcher_mode: opt.value })}
                                        />
                                        <div>
                                            <div className="text-[11px] font-medium text-slate-800">{opt.label}</div>
                                            <div className="text-[10px] text-slate-500 leading-snug">{opt.hint}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <LauncherAbStatsWidget
                                linkId={link?.link_id}
                                scope={scope}
                                abEnabled={f.manual_upi_mode && f.manual_upi_launcher_mode === "ab_test"}
                            />
                        </div>
                        <label className={`flex items-start gap-2 cursor-pointer pl-6 ${f.manual_upi_mode ? "" : "opacity-50"}`}>
                            <input
                                id="lf-manual-upi-recopy"
                                data-testid="lf-manual-upi-recopy"
                                type="checkbox"
                                className="mt-0.5"
                                disabled={!f.manual_upi_mode}
                                checked={f.manual_upi_recopy_pill}
                                onChange={(e) => setF({ ...f, manual_upi_recopy_pill: e.target.checked })}
                            />
                            <div>
                                <div className="text-xs font-medium text-slate-800">Show &quot;Copy UPI ID again&quot; pill on return</div>
                                <div className="text-[10px] text-slate-500 leading-snug">When the customer returns from a UPI app, surface a one-tap recopy pill in the bottom-right. Useful because some apps (notably Paytm) clear the system clipboard on launch — this lets the customer recover their VPA in one tap. Requires Manual UPI mode.</div>
                            </div>
                        </label>
                    </div>
                </CollapsibleSection>

                <CollapsibleSection
                    testid="section-anti-copy"
                    title="Anti-copy protection"
                    subtitle="customer-facing protections"
                    defaultOpen={false}
                    summary={(() => {
                        const cur = {
                            a: !!f.qr_anti_screenshot,
                            h: !!f.qr_hold_to_reveal,
                            u: !!f.hide_upi_id,
                        };
                        if (!cur.a && !cur.h && !cur.u) return "Open · fastest checkout";
                        if (cur.a && !cur.h && !cur.u) return "Standard · recommended";
                        if (cur.a && cur.h && cur.u) return "Locked-down · all protections on";
                        return "Custom · fine-tuned flags";
                    })()}
                >
                    <ProtectionPresetSelector form={f} setForm={setF} />
                </CollapsibleSection>

                <div className="hidden">{/* Anti-copy section now self-contained */}</div>

                <CollapsibleSection
                    testid="section-payment-methods"
                    title="Payment methods"
                    subtitle="lock to UPI or Bank only"
                    defaultOpen={false}
                    summary={(() => {
                        const m = f.enabled_methods || "all";
                        if (m === "upi_only") return "UPI only · bank block hidden";
                        if (m === "bank_only") return "Bank only · UPI block hidden";
                        return "Both UPI & Bank · smart routing";
                    })()}
                >
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { v: "all", t: "Both UPI & Bank", d: "Smart routing decides per amount" },
                            { v: "upi_only", t: "UPI only", d: "Hide bank transfer block" },
                            { v: "bank_only", t: "Bank only", d: "Hide UPI block" },
                        ].map((opt) => (
                            <button key={opt.v} type="button"
                                    data-testid={`lf-method-${opt.v}`}
                                    onClick={() => setF({ ...f, enabled_methods: opt.v })}
                                    className={`text-left rounded-md border-2 px-3 py-2 text-xs transition-colors ${
                                        f.enabled_methods === opt.v
                                            ? "border-blue-500 bg-blue-50"
                                            : "border-slate-200 bg-white hover:border-slate-300"
                                    }`}>
                                <div className={`font-medium ${f.enabled_methods === opt.v ? "text-blue-900" : "text-slate-800"}`}>{opt.t}</div>
                                <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{opt.d}</div>
                            </button>
                        ))}
                    </div>
                </CollapsibleSection>

                <CollapsibleSection
                    testid="section-channel-allowlist"
                    title="Channel allowlist"
                    subtitle="empty = use all"
                    defaultOpen={false}
                    summary={(() => {
                        const upiN = (f.allowed_upi_channels || []).length;
                        const bankN = (f.allowed_bank_accounts || []).length;
                        if (upiN === 0 && bankN === 0) return "All channels available · default routing";
                        const parts = [];
                        parts.push(upiN === 0 ? "all UPI" : `${upiN} UPI`);
                        parts.push(bankN === 0 ? "all bank" : `${bankN} bank`);
                        return parts.join(" · ");
                    })()}
                >
                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                            <div className="font-medium text-slate-600 mb-1">UPI channels</div>
                            <div className="space-y-1 max-h-40 overflow-auto">
                                {channels.upi.length === 0 && <div className="text-slate-400 text-[11px]">None</div>}
                                {channels.upi.map((c) => (
                                    <label key={c.channel_id} className="flex items-center gap-1.5 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                                        <input type="checkbox" data-testid={`lf-upi-${c.channel_id}`}
                                               checked={f.allowed_upi_channels.includes(c.channel_id)}
                                               onChange={() => toggleChan("upi", c.channel_id)} />
                                        <span className="truncate">{c.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="font-medium text-slate-600 mb-1">Bank accounts</div>
                            <div className="space-y-1 max-h-40 overflow-auto">
                                {channels.bank.length === 0 && <div className="text-slate-400 text-[11px]">None</div>}
                                {channels.bank.map((c) => (
                                    <label key={c.channel_id} className="flex items-center gap-1.5 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                                        <input type="checkbox" data-testid={`lf-bank-${c.channel_id}`}
                                               checked={f.allowed_bank_accounts.includes(c.channel_id)}
                                               onChange={() => toggleChan("bank", c.channel_id)} />
                                        <span className="truncate">{c.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </CollapsibleSection>

                {/* iter-66: Deposit-count allow-list. Optional per-link
                    filter — empty (default) preserves existing "any bucket"
                    behaviour. Populated buckets are enforced BEFORE channel
                    selection so a loyalty link with [9] only accepts the
                    customer's exact 10th deposit. */}
                <CollapsibleSection
                    testid="section-deposit-counts"
                    title="Deposit-count filter"
                    subtitle="restrict this link to specific deposit numbers"
                    defaultOpen={false}
                    summary={
                        ((f.allowed_deposit_counts || []).length === 0 && f.min_deposit_count == null)
                            ? "Any deposit-count accepted"
                            : `Only: ${fmtDepositCats(f.allowed_deposit_counts, f.min_deposit_count)}`
                    }
                >
                    <p className="text-xs text-slate-500 mb-2">
                        Leave empty to accept any customer. Populate to restrict
                        this link to customers whose current successful-deposit
                        index matches one of the selected buckets — useful for
                        one-shot loyalty links or FTD-only promos. Add
                        <span className="font-mono"> + N and above </span>
                        for an open-ended threshold like "10th deposit onwards".
                    </p>
                    <DepositCountPicker
                        testId="link-dcat"
                        value={f.allowed_deposit_counts || []}
                        onChange={(next) => setF({ ...f, allowed_deposit_counts: next })}
                        threshold={f.min_deposit_count ?? null}
                        onThresholdChange={(n) => setF({ ...f, min_deposit_count: n })}
                    />
                </CollapsibleSection>

                {/* Websites — per-link allowlist + default. Surfaced at the
                    customer's checkout as a chooser. Empty allowlist = show
                    all partner websites. Default is pre-selected (still
                    overridable by the customer unless only one is shown). */}
                <CollapsibleSection
                    testid="section-websites"
                    title="Websites at checkout"
                    subtitle="per-link allowlist + default"
                    defaultOpen={false}
                    summary={(() => {
                        const n = (f.allowed_website_ids || []).length;
                        const def = (websites || []).find((w) => w.website_id === f.default_website_id);
                        if (n === 0 && !def) return "All websites · no default";
                        const parts = [];
                        parts.push(n === 0 ? "all websites" : `${n} allowed`);
                        if (def) parts.push(`default: ${def.name}`);
                        return parts.join(" · ");
                    })()}
                >
                    {(websites || []).length === 0 ? (
                        <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-3">
                            You haven&apos;t added any websites yet. <Link to="/partner/websites" className="underline font-medium">Add some on the Websites page</Link> to enable per-link selection.
                        </div>
                    ) : (
                        <div className="space-y-3 text-xs">
                            <div>
                                <div className="font-medium text-slate-600 mb-1.5">Allowed websites <span className="text-slate-400 font-normal">(empty = all)</span></div>
                                <div className="space-y-1 max-h-40 overflow-auto">
                                    {websites.map((w) => (
                                        <label key={w.website_id} className="flex items-center gap-1.5 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                                            <input
                                                type="checkbox"
                                                data-testid={`lf-website-allow-${w.website_id}`}
                                                checked={(f.allowed_website_ids || []).includes(w.website_id)}
                                                onChange={() => {
                                                    const cur = f.allowed_website_ids || [];
                                                    setF({ ...f,
                                                        allowed_website_ids: cur.includes(w.website_id)
                                                            ? cur.filter((id) => id !== w.website_id)
                                                            : [...cur, w.website_id],
                                                    });
                                                }}
                                            />
                                            <span className="truncate">{w.name}</span>
                                            {w.status !== "active" && <span className="text-[9px] text-amber-600 uppercase">(disabled)</span>}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="font-medium text-slate-600 mb-1">Default website (pre-selected at checkout)</div>
                                <select
                                    data-testid="lf-default-website"
                                    value={f.default_website_id || ""}
                                    onChange={(e) => setF({ ...f, default_website_id: e.target.value || null })}
                                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs bg-white"
                                >
                                    <option value="">— no default —</option>
                                    {(websites || [])
                                        .filter((w) => {
                                            const allow = f.allowed_website_ids || [];
                                            return allow.length === 0 || allow.includes(w.website_id);
                                        })
                                        .map((w) => (
                                            <option key={w.website_id} value={w.website_id}>{w.name}</option>
                                        ))}
                                </select>
                                <div className="text-[10px] text-slate-500 mt-1 leading-snug">
                                    Customers can override the default unless the chooser is hidden (single website configured). Auto-suggest also kicks in: if the customer has a prior successful payment on this link with a specific website, that one is pre-selected instead of the default.
                                </div>
                            </div>
                        </div>
                    )}
                </CollapsibleSection>

                <LinkExperimentsPanel linkId={link?.link_id} scope={scope} />

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md">Cancel</button>
                    <button data-testid="lf-save" type="submit" disabled={busy}
                            className="btn-primary px-4 py-2 rounded-md flex items-center gap-2">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {isNew ? "Create link" : "Save changes"}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
